import React, { useEffect, useState } from 'react';

import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { ChartsXAxis, LineChart } from '@mui/x-charts';
import { axisClasses } from '@mui/x-charts/ChartsAxis';

import { ArchivedSot, Province } from '../../../@types/KingdomResponse';
import { useIntelState } from '../../../context/IntelState';
import RestUtilities from '../../../services/RestUtilities';

interface Props {
  province: Province;
}

const missingDataFormatter = (value: number | null) =>
  value == null ? 'NaN' : value.toString();

const ProvinceGraphs: React.FC<Props> = ({ province }) => {
  const { intelState } = useIntelState();

  const [data, setData] = useState<ArchivedSot[] | undefined>(undefined);
  const [labels, setLabels] = useState<string[]>(['land']);

  useEffect(() => {
    const getData = async () => {
      const response = await RestUtilities.get(
        `Province/v1/SotArchive?server=${intelState.server}&location=${province.location}&slot=${province.slot}`
      );
      if (response.ok) {
        setData(response.content);
      }
    };
    if (!data) {
      getData();
    }
  }, [data, intelState.server, province.location, province.slot]);

  const getDataSeries = (field: string) => {
    return data?.map((x: ArchivedSot | undefined) => (x ? x[field] : null));
  };

  const handleLabelClick = (label: string) => {
    if (labels.includes(label)) {
      const newLabels = labels.filter((x) => x !== label);
      setLabels(newLabels);
    } else {
      const newLabels = [...labels];
      newLabels.push(label);
      setLabels(newLabels);
    }
  };
  const getCheckBoxes = () => {
    if (!data || data.length == 0) return null;
    const sliced = Object.keys(data[0]).filter(
      (key) => key !== 'tickId' && key !== 'tickName'
    );
    return sliced.map((key) => {
      return (
        <Grid item key={key} sx={{ px: 1, textAlign: 'center' }}>
          <Box style={{ fontSize: '10px' }}>{key.toUpperCase()}</Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              size='small'
              checked={labels.includes(key)}
              onClick={() => handleLabelClick(key)}
            />
          </Box>
        </Grid>
      );
    });
  };
  if (!data || data.length == 0) return null;

  return (
    <Box>
      <Typography variant='body1' my={2} textAlign='center'>
        SoTs over last 72 ticks
      </Typography>
      <Grid
        container
        columns={{ sm: 4, md: 8 }}
        sx={{ mb: 0, display: 'flex', justifyContent: 'center' }}
      >
        {getCheckBoxes()}
      </Grid>

      <Box sx={{ width: '100%' }}>
        <LineChart
          xAxis={[
            {
              data: data.map((x) => x.tickId),
              id: 'ticks',
            },
          ]}
          yAxis={[{ id: 'amount', scaleType: 'linear' }]}
          series={labels.map((field: string) => {
            return {
              data: getDataSeries(field),
              label: field.toUpperCase(),
            };
          })}
          height={labels.length ? 300 : 100}
          margin={{ left: 60, right: 20 }}
          sx={{
            [`.${axisClasses.left} .${axisClasses.label}`]: {
              transform: 'translate(-25px, 0)',
            },
          }}
          slotProps={{
            legend: {
              labelStyle: {
                fontSize: 12,
                textTransform: 'capitalize',
              },
            },
          }}
        >
          <ChartsXAxis axisId='ticks' label='Tick Number' />
        </LineChart>
      </Box>
    </Box>
  );
};

export default ProvinceGraphs;
